// Product Swap route picker — choose a scene where a model/hand holds a
// placeholder product. The user's product is swapped into the placeholder
// region via the backend's mask-based edit pipeline (is_product_swap).
//
// Scenes are grouped by how well their held placeholder matches the
// uploaded product's silhouette (from analyze-product's productSilhouette).
// When we have a detected silhouette + matching scenes, a "Best for your
// product" group leads; everything else falls under "All scenes". Without a
// detected silhouette we just show every scene.
//
// Thumbnails are 1:1 (the held shots are 1024²), so cards are aspect-square.
import { memo, useCallback, useMemo } from 'react';
import {
  productSwapScenes,
  SILHOUETTE_LABELS,
  type ProductSwapScene,
  type ProductSilhouette,
} from '@/mocks/app-data';
import StepHeader from './StepHeader';

interface SceneCardProps {
  scene: ProductSwapScene;
  isActive: boolean;
  onSelect: (id: string) => void;
}

const SceneCard = memo(function SceneCard({ scene, isActive, onSelect }: SceneCardProps) {
  const handleClick = useCallback(() => onSelect(scene.id), [scene.id, onSelect]);

  return (
    <button
      onClick={handleClick}
      className={`group relative rounded-xl overflow-hidden border-2 text-left cursor-pointer aspect-square w-full active:scale-[0.98] ${
        isActive
          ? 'border-primary shadow-lg shadow-primary/15'
          : 'border-transparent hover:border-surface-dark/50 bg-surface hover:shadow-sm'
      }`}
    >
      <img
        src={scene.thumb}
        alt={scene.name}
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
      />
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-dark/80 to-transparent pointer-events-none" />

      <div className="absolute bottom-2 left-2 right-2">
        <p className="text-xs font-bold text-white leading-tight">{scene.name}</p>
        <p className="text-[11px] text-white/70 leading-tight">{scene.vibe}</p>
      </div>

      {isActive && (
        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
          <i className="ri-check-line text-white text-xs" />
        </div>
      )}
    </button>
  );
});

interface StepProductSwapSceneProps {
  selected: string | null;
  onSelect: (id: string) => void;
  /** Detected product silhouette from analyze-product. When present and
   *  some scenes match it, those lead under a "Best for your product"
   *  heading. Optional — without it every scene shows in one grid. */
  productSilhouette?: ProductSilhouette | null;
  /** /appv2's Section wrapper supplies its own header and the page scrolls
   *  naturally — drop h-full + the inner scroll wrapper. */
  compact?: boolean;
}

export default function StepProductSwapScene({
  selected,
  onSelect,
  productSilhouette,
  compact,
}: StepProductSwapSceneProps) {
  const selectedScene = productSwapScenes.find((s) => s.id === selected);

  // Split scenes into "matches the detected silhouette" vs the rest. When
  // there's no detected silhouette (or nothing matches), `recommended` is
  // empty and we render a single "All scenes" grid.
  const { recommended, rest } = useMemo(() => {
    if (!productSilhouette || productSilhouette === 'other') {
      return { recommended: [] as ProductSwapScene[], rest: productSwapScenes };
    }
    const rec = productSwapScenes.filter((s) => s.silhouettes.includes(productSilhouette));
    const recIds = new Set(rec.map((s) => s.id));
    return { recommended: rec, rest: productSwapScenes.filter((s) => !recIds.has(s.id)) };
  }, [productSilhouette]);

  const hasRecommended = recommended.length > 0;

  const grid = (scenes: ProductSwapScene[]) => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
      {scenes.map((scene) => (
        <SceneCard
          key={scene.id}
          scene={scene}
          isActive={selected === scene.id}
          onSelect={onSelect}
        />
      ))}
    </div>
  );

  return (
    <div className={compact ? '' : 'h-full flex flex-col'}>
      {!compact && (
        <StepHeader
          icon="ri-hand-heart-line"
          title="Choose a Scene"
          subtitle="Pick a shot where a model holds a product. We'll swap your product into their hand, keeping the pose, lighting, and background."
        />
      )}

      <div className={compact ? '' : 'flex-1 min-h-0 overflow-y-auto'}>
        {hasRecommended ? (
          <div className="space-y-5">
            <div>
              <div className="flex items-center gap-2 mb-2.5">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold">
                  <i className="ri-sparkling-2-line" />
                  Best for your {SILHOUETTE_LABELS[productSilhouette!].toLowerCase()}
                </span>
              </div>
              {grid(recommended)}
            </div>
            {rest.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-dark-muted mb-2.5">All scenes</p>
                {grid(rest)}
              </div>
            )}
          </div>
        ) : (
          grid(rest)
        )}
      </div>

      {selectedScene && (
        <div className="flex-shrink-0 flex items-center justify-center gap-2 pt-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-light border border-primary/20">
            <i className="ri-check-line text-primary text-xs" />
            <span className="text-xs font-semibold text-primary">{selectedScene.name}</span>
            <span className="text-xs text-dark-muted">{selectedScene.vibe}</span>
          </div>
        </div>
      )}
    </div>
  );
}
